/**
 * Custom hook for fetching admin dashboard metrics
 * Feature: 003-admin-dashboard-metrics
 * Updated: 007-cloudflare-migration
 *
 * Fetches metric counts for users, businesses, jobs, and marketplace via API Gateway.
 * Provides loading states, error handling, and retry functionality per metric.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { MetricsState, MetricData } from '../types/adminMetrics';
import {
  getUsersMetrics,
  getBusinessesMetrics,
  getJobsMetrics,
  getMarketplaceMetrics,
} from '../services/adminMetrics';
import { isApiError, getErrorMessage } from '../services/api/errors';

type MetricType = 'users' | 'businesses' | 'jobs' | 'marketplace';

interface UseAdminMetricsReturn {
  metrics: MetricsState | null;
  loading: boolean;
  errors: Record<MetricType, string | null>;
  refetch: (metric: MetricType) => Promise<void>;
  refetchAll: () => Promise<void>;
}

/**
 * Hook to fetch and manage admin metrics
 * Fetches all four metrics in parallel on mount
 * Supports individual metric retry on error
 */
export function useAdminMetrics(): UseAdminMetricsReturn {
  const { t } = useTranslation('common');
  const [metrics, setMetrics] = useState<MetricsState | null>(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<Record<MetricType, string | null>>({
    users: null,
    businesses: null,
    jobs: null,
    marketplace: null,
  });

  const getLocalizedError = useCallback(
    (error: unknown): string => {
      if (isApiError(error)) {
        return getErrorMessage(error, t);
      }
      return error instanceof Error ? error.message : t('api.errors.unknown');
    },
    [t]
  );

  const fetchMetric = useCallback(
    async (type: MetricType, fetchFn: () => Promise<MetricData>) => {
      try {
        setErrors((prev) => ({ ...prev, [type]: null }));
        const data = await fetchFn();
        setMetrics((prev) => (prev ? { ...prev, [type]: data } : null));
      } catch (error) {
        const errorMessage = getLocalizedError(error);
        setErrors((prev) => ({ ...prev, [type]: errorMessage }));
      }
    },
    [getLocalizedError]
  );

  const fetchAllMetrics = useCallback(async () => {
    setLoading(true);

    // Fetch all metrics in parallel
    const results = await Promise.allSettled([
      getUsersMetrics(),
      getBusinessesMetrics(),
      getJobsMetrics(),
      getMarketplaceMetrics(),
    ]);

    const newMetrics: MetricsState = {
      users: { active: 0, archived: 0 },
      businesses: { active: 0, archived: 0 },
      jobs: { active: 0, archived: 0 },
      marketplace: { active: 0, archived: 0 },
    };

    const newErrors: Record<MetricType, string | null> = {
      users: null,
      businesses: null,
      jobs: null,
      marketplace: null,
    };

    // Process users result
    if (results[0].status === 'fulfilled') {
      newMetrics.users = results[0].value;
    } else {
      newErrors.users = getLocalizedError(results[0].reason);
    }

    // Process businesses result
    if (results[1].status === 'fulfilled') {
      newMetrics.businesses = results[1].value;
    } else {
      newErrors.businesses = getLocalizedError(results[1].reason);
    }

    // Process jobs result
    if (results[2].status === 'fulfilled') {
      newMetrics.jobs = results[2].value;
    } else {
      newErrors.jobs = getLocalizedError(results[2].reason);
    }

    // Process marketplace result
    if (results[3].status === 'fulfilled') {
      newMetrics.marketplace = results[3].value;
    } else {
      newErrors.marketplace = getLocalizedError(results[3].reason);
    }

    setMetrics(newMetrics);
    setErrors(newErrors);
    setLoading(false);
  }, [getLocalizedError]);

  const refetch = useCallback(
    async (metric: MetricType) => {
      const fetchFunctions: Record<MetricType, () => Promise<MetricData>> = {
        users: getUsersMetrics,
        businesses: getBusinessesMetrics,
        jobs: getJobsMetrics,
        marketplace: getMarketplaceMetrics,
      };

      await fetchMetric(metric, fetchFunctions[metric]);
    },
    [fetchMetric]
  );

  const refetchAll = useCallback(async () => {
    await fetchAllMetrics();
  }, [fetchAllMetrics]);

  // Fetch all metrics on mount
  useEffect(() => {
    fetchAllMetrics();
  }, [fetchAllMetrics]);

  return {
    metrics,
    loading,
    errors,
    refetch,
    refetchAll,
  };
}
